import { useGetList } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type Notification = {
  id: number;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: number | null;
  read_at: string | null;
  created_at: string;
};

const linkFor = (n: Notification) => {
  switch (n.entity_type) {
    case "ticket":
      return `/tickets/${n.entity_id}/show`;
    case "project":
      return `/projects/${n.entity_id}/show`;
    default:
      return null;
  }
};

const ago = (iso: string) => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

/**
 * The dashboard's right-hand column.
 *
 * The same inbox as the bell, laid out to be read rather than glanced at. It
 * needs no permission check: RLS restricts rows to the recipient, so whatever
 * a viewer's role, this shows their own and only their own.
 */
export const DashboardNotifications = () => {
  const { data, isPending } = useGetList<Notification>("notifications", {
    pagination: { page: 1, perPage: 12 },
    sort: { field: "created_at", order: "DESC" },
  });

  if (isPending) return null;
  const notifications = data ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Recent activity</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            Nothing yet. Assignments, tickets and project changes appear here.
          </p>
        ) : (
          <div className="divide-y">
            {notifications.map((n) => {
              const to = linkFor(n);
              const row = (
                <div className="py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium">{n.title}</span>
                    {n.read_at ? null : (
                      <Badge variant="secondary" className="shrink-0">
                        new
                      </Badge>
                    )}
                  </div>
                  {n.body ? (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {n.body}
                    </p>
                  ) : null}
                  <p className="text-xs text-muted-foreground pt-0.5">
                    {ago(n.created_at)}
                  </p>
                </div>
              );
              return to ? (
                <Link key={n.id} to={to} className="block hover:bg-accent/40">
                  {row}
                </Link>
              ) : (
                <div key={n.id}>{row}</div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
